import React from "react";
import {
    faSparkles,
    faSquareTerminal,
} from "@fortawesome/pro-regular-svg-icons";
import { flex } from "@singlestore/fusion/components/layout";
import useMediaQuery from "@singlestore/fusion/react-utils/use-media-query";
import { BREAKPOINTS } from "@singlestore/fusion/tokens/js/variables/supplementary";
import { useFlyout } from "contexts/FlyoutContext";
import { Script } from "gatsby";

import { Button } from "components/Button/Button";
import { VerifySignatureModal } from "components/DownloadButton/VerifySignatureModal";
import { FaIcon } from "components/FaIcon/FaIcon";
import { KetchPrivacyCenter } from "components/KetchPrivacyCenter/KetchPrivacyCenter";
import { Navigation } from "components/layout/Navigation/Navigation";
import { Paragraph } from "components/Paragraph/Paragraph";
import { SearchModal } from "components/SearchModal/SearchModal";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "components/Tooltip/Tooltip";

import { getEnv } from "utils/get-env";
import { RECAPTCHA_PUBLIC_KEY } from "utils/recaptcha";
import { track } from "utils/segment";

import "scss/global.scss";
import "./BaseLayout.scss";

const SQRL_TOOLTIP_STATE_KEY = "sqrl_tooltip_state";

type BaseLayoutProps = {};

export function BaseLayout({
    children,
}: React.PropsWithChildren<BaseLayoutProps>) {
    const isMdAndAbove = useMediaQuery(BREAKPOINTS["md-n-above"]);
    const [sqrlLargeTooltipOpen, setSqrlLargeTooltipOpen] =
        React.useState<boolean>(false);

    const env = getEnv();
    const { tab, setTab } = useFlyout();

    React.useEffect(() => {
        const sqrlTooltipState = localStorage.getItem(SQRL_TOOLTIP_STATE_KEY);
        if (!sqrlTooltipState) {
            // if there is no state user has not interacted with the tooltip
            setSqrlLargeTooltipOpen(true);
        }
    }, []);

    const handlePlaygroundToggle = () => {
        setTab((tab) => {
            if (tab === "playground") {
                return null;
            }

            track("Playground Session Initiated", {
                method: "Sidebar",
            });

            return "playground";
        });
    };

    const handleSqrlToggle = () => {
        setSqrlLargeTooltipOpen(false);

        setTab((tab) => {
            if (tab === "sqrl") {
                return null;
            }

            track("SQrL Conversation Initiated", {
                method: "Sidebar",
            });

            localStorage.setItem(SQRL_TOOLTIP_STATE_KEY, "opened");

            return "sqrl";
        });
    };

    const handleSqrlDismiss = () => {
        setSqrlLargeTooltipOpen(false);
        localStorage.setItem(SQRL_TOOLTIP_STATE_KEY, "closed");
    };

    let sidebarControl;
    if (!tab) {
        const sqrlTooltipClasses = flex({
            flexDirection: "column",
            gap: "1x",
            alignItems: "start",
        });

        const sqrlButtonToRender = (
            <Button
                className="playground-toggle-button"
                variation="ghost-secondary"
                size="lg"
                aria-label="Open SQrL Bot"
                onClick={handleSqrlToggle}
            >
                <FaIcon icon={faSparkles} />
            </Button>
        );

        let sqrlButtonWithTooltipToRender;
        if (sqrlLargeTooltipOpen) {
            sqrlButtonWithTooltipToRender = (
                <Tooltip open={isMdAndAbove}>
                    <TooltipTrigger>{sqrlButtonToRender}</TooltipTrigger>
                    <TooltipContent side="right" className={sqrlTooltipClasses}>
                        <Paragraph>
                            Check out the new way to search with SQrL bot
                        </Paragraph>

                        <Button
                            variation="secondary"
                            onClick={handleSqrlDismiss}
                        >
                            Dismiss
                        </Button>
                    </TooltipContent>
                </Tooltip>
            );
        } else {
            sqrlButtonWithTooltipToRender = (
                <Tooltip>
                    <TooltipTrigger>{sqrlButtonToRender}</TooltipTrigger>
                    <TooltipContent side="right">
                        <Paragraph>Ask SQrL, our AI assistant</Paragraph>
                    </TooltipContent>
                </Tooltip>
            );
        }

        sidebarControl = (
            <div className="sidebar-control">
                <Tooltip>
                    <TooltipTrigger>
                        <Button
                            className="playground-toggle-button"
                            variation="ghost-secondary"
                            size="lg"
                            aria-label="Open interactive query playground"
                            onClick={handlePlaygroundToggle}
                        >
                            <FaIcon icon={faSquareTerminal} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                        <Paragraph>Open query playground</Paragraph>
                    </TooltipContent>
                </Tooltip>

                {sqrlButtonWithTooltipToRender}
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            {env !== "development" && (
                <Script
                    id="ketch-script"
                    type="text/javascript"
                    src="/ketch.js"
                />
            )}

            {env !== "development" && (
                <Script
                    defer
                    src="https://cdn.usefathom.com/script.js"
                    data-site="PNAAAMUA"
                />
            )}

            {env !== "development" && (
                <Script
                    key="recaptcha-script"
                    defer
                    src={`https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_PUBLIC_KEY}`}
                />
            )}

            {env !== "development" && (
                <Script key="intercom-script" defer src="/load-intercom.js" />
            )}

            <Navigation />

            <div className="content">
                {sidebarControl}

                <div className="container">{children}</div>
            </div>

            <SearchModal />
            <VerifySignatureModal />
            <KetchPrivacyCenter />
        </div>
    );
}
